import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { catalogTrust } from '@bb/contracts';
import { Button, ConfirmDialog, EmptyState, ErrorState, Modal, ReadOnlyBanner } from '@bb/ui';
import { ApiError, type TrustItem, type WritePolicy } from '../api/client.ts';
import { usePermissions } from '../features/access/usePermissions.ts';

export const WRITE_POLICY_LABEL: Record<WritePolicy, string> = { any: 'Any source', server_only_report: 'Backend only (report)', server_only: 'Backend only' };
const RANK: Record<WritePolicy, number> = { any: 0, server_only_report: 1, server_only: 2 };
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const ROUTE = '/env/$environmentId/data/trust';

type Change = { item: TrustItem; to: WritePolicy };

/**
 * Data → Trusted data (BB-33): which attributes and events only the backend may write, what clients still write by app
 * version, and when report mode can be enforced without dropping updates.
 */
export function TrustedDataPage() {
  const { environmentId } = useParams({ from: ROUTE });
  const { api } = useRouteContext({ from: ROUTE });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has('catalog.edit');
  const [days, setDays] = useState(7);
  const report = useQuery({ queryKey: ['trust', environmentId, days], queryFn: () => api.trustReport(environmentId, days) });
  const [change, setChange] = useState<Change | null>(null);
  const [reason, setReason] = useState('');
  const apply = useMutation({
    mutationFn: ({ item, to }: Change) => {
      const patch = { writePolicy: to, ...(reason.trim() ? { reason: reason.trim() } : {}) };
      return item.kind === 'attribute' ? api.updateAttribute(environmentId, item.name, patch) : api.updateEvent(environmentId, item.name, patch);
    },
    onSuccess: () => {
      setChange(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['trust', environmentId] });
    },
  });
  const loosening = change ? RANK[change.to] < RANK[change.item.writePolicy] : false;
  const reasonShort = loosening && reason.trim().length < catalogTrust.WRITE_POLICY_REASON_MIN;

  return (
    <section>
      <h1>Trusted data</h1>
      <p className="hint">Attributes and events that only the backend may write. Clients cannot confirm deposits, KYC or account state.</p>
      {!canEdit && <ReadOnlyBanner>Changing write policies needs the catalog.edit permission.</ReadOnlyBanner>}
      <div className="filters">
        <label>
          Window{' '}
          <select aria-label="Window" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 14, 30].map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        </label>
      </div>
      {report.isPending ? (
        <p aria-busy="true">Loading trusted data…</p>
      ) : report.isError ? (
        <ErrorState title="Trusted data could not be loaded" message={errorText(report.error)} onRetry={() => void report.refetch()} />
      ) : report.data.items.length === 0 ? (
        <EmptyState title="Nothing is restricted" description="No attribute or event is limited to the backend, and no client wrote a restricted one." />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Kind</th>
              <th scope="col">Policy</th>
              <th scope="col">Client writes reported</th>
              <th scope="col">Refused</th>
              <th scope="col">App versions</th>
              <th scope="col">Enforce</th>
            </tr>
          </thead>
          <tbody>
            {report.data.items.map((item) => (
              <tr key={`${item.kind}:${item.name}`}>
                <td>
                  <code>{item.name}</code>
                </td>
                <td>{item.kind}</td>
                <td>
                  {canEdit ? (
                    <select aria-label={`Write policy of ${item.name}`} value={item.writePolicy} onChange={(e) => setChange({ item, to: e.target.value as WritePolicy })}>
                      {(Object.keys(WRITE_POLICY_LABEL) as WritePolicy[]).map((p) => (
                        <option key={p} value={p}>
                          {WRITE_POLICY_LABEL[p]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    WRITE_POLICY_LABEL[item.writePolicy]
                  )}
                </td>
                <td>{item.violations.reported}</td>
                <td>{item.violations.rejected}</td>
                <td>
                  {item.byAppVersion.length === 0
                    ? '—'
                    : item.byAppVersion.map((v) => `${v.appVersion || 'server'} (${v.reported + v.rejected})`).join(', ')}
                </td>
                <td>
                  {item.readyToEnforce ? (
                    canEdit ? (
                      <Button size="sm" variant="primary" onClick={() => setChange({ item, to: 'server_only' })}>
                        Enforce {item.name}
                      </Button>
                    ) : (
                      <span className="tag tag--neutral">Ready</span>
                    )
                  ) : item.writePolicy === 'server_only_report' ? (
                    <span className="hint">Clients still write it</span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {change && !loosening && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setChange(null)}
          title={`${WRITE_POLICY_LABEL[change.to]}: ${change.item.name}`}
          confirmLabel="Apply"
          tone="primary"
          busy={apply.isPending}
          onConfirm={() => apply.mutate(change)}
        >
          {change.to === 'server_only'
            ? 'From now on, writes from apps and Canvas are refused with trusted_attribute_forbidden / trusted_event_forbidden.'
            : 'Writes from apps are still accepted but reported here.'}
          {apply.isError && <p role="alert">{errorText(apply.error)}</p>}
        </ConfirmDialog>
      )}
      {change && loosening && (
        <Modal
          open
          onOpenChange={(open) => !open && setChange(null)}
          title={`Loosen ${change.item.name} to ${WRITE_POLICY_LABEL[change.to]}`}
          footer={
            <>
              <Button variant="secondary" onClick={() => setChange(null)}>
                Cancel
              </Button>
              <Button variant="primary" disabled={reasonShort || apply.isPending} onClick={() => apply.mutate(change)}>
                Loosen
              </Button>
            </>
          }
        >
          <label>
            Reason
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} aria-describedby="trust-reason-hint" />
          </label>
          <p id="trust-reason-hint" className="hint">
            At least {catalogTrust.WRITE_POLICY_REASON_MIN} characters; recorded in the audit log.
          </p>
          {apply.isError && <p role="alert">{errorText(apply.error)}</p>}
        </Modal>
      )}
    </section>
  );
}
